import React, { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { IReviewNew } from '../../shared/interfaces/IReviewNew';
import {
  alpha,
  Box,
  hexToRgb,
  Pagination,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { ITrustpageFilter } from '../../shared/interfaces/ITrustpageFilter';
import { ITrustpageMonitoring } from '../../shared/interfaces/ITrustpageMonitoring';
import TrustpageFilterSort from '../TrustpageFilterSort/TrustpageFilterSort';
import FacebookReview from '../Review/Type/FacebookReview';
import GolocalReview from '../Review/Type/GolocalReview';
import AerzteReview from '../Review/Type/AerzteReview';
import EKomiReview from '../Review/Type/EKomiReview';
import KununuReview from '../Review/Type/KununuReview';
import HolidayCheckReview from '../Review/Type/HolidayCheckReview';
import TripAdvisorReview from '../Review/Type/TripAdvisorReview';
import HotelsReview from '../Review/Type/HotelsReview';
import AirbnbReview from '../Review/Type/AirbnbReview';
import IndeedReview from '../Review/Type/IndeedReview';
import ExpediaReview from '../Review/Type/ExpediaReview';
import BookingReview from '../Review/Type/BookingReview';
import IdealoReview from '../Review/Type/IdealoReview';
import TrustedShopsReview from '../Review/Type/TrustedShopsReview';
import SanegoReview from '../Review/Type/SanegoReview';
import YelpReview from '../Review/Type/YelpReview';
import TrustPilotReview from '../Review/Type/TrustPilotReview';
import JamedaReview from '../Review/Type/JamedaReview';
import GlassdoorReview from '../Review/Type/GlassdoorReview';
import ShopauskunftReview from '../Review/Type/ShopauskunftReview';
import AmazondeReview from '../Review/Type/AmazondeReview';
import PlaystoreReview from '../Review/Type/PlaystoreReview';
import AppstoreReview from '../Review/Type/AppstoreReview';
import TrustpageReviewCount from '../TrustpageReviewCount/TrustpageReviewCount';
import TrustpageService from '../../services/TrustpageService';
import GoogleReview from '../Review/Type/GoogleReview';
import MobileReview from '../Review/Type/MobileReview';
import AutoscoutReview from '../Review/Type/AutoscoutReview';
import TellowsReview from '../Review/Type/TellowsReview';
import GooglePlace from '../Review/Type/GooglePlace';
import Check24 from '../Review/Type/Check24';
import EbayReview from '../Review/Type/EbayReview';
import CapterraReview from '../Review/Type/CapterraReview';
import FewoReview from '../Review/Type/FewoReview';
import GutebankenReview from '../Review/Type/GutebankenReview';
import JobvotingReview from '../Review/Type/JobvotingReview';
import TrivagoReview from '../Review/Type/TrivagoReview';
import OmrReview from '../Review/Type/OmrReview';

interface Props {
  trustpageId: string;
  monitorings: ITrustpageMonitoring[];
  primaryColor: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  commentingModule: boolean;
}

interface TrustpageReviewsPaging {
  pageSize: number;
  offset: number;
  totalItems: number;
  currentPage: number;
  pagesTotal: number;
}

const TrustpageReviews: FC<Props> = ({
  trustpageId,
  monitorings,
  primaryColor,
  setLoading,
  commentingModule,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('md'));

  const [reviews, setReviews] = useState<IReviewNew[]>([]);
  const [reviewsResponse, setReviewsResponse] = useState<TrustpageReviewsPaging>({
    pageSize: 0,
    offset: 0,
    totalItems: 0,
    currentPage: 1,
    pagesTotal: 1,
  });

  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<ITrustpageFilter>({
    offset: 0,
    pageSize: 10,
    orderBy: ['score desc'],
  });

  useEffect(() => {
    const controller = new AbortController();
    const getReviews = async () => {
      const response = await TrustpageService.getTrustpageReviewsPublic(
        trustpageId,
        setLoading,
        controller.signal,
        filter
      );
      if (!response) return;
      setReviews(response.reviews);
      setReviewsResponse(response.paging);
    };

    (async () => await getReviews())();

    return () => {
      controller.abort();
    };
  }, [setLoading, trustpageId, filter]);

  const handleOrderByChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const values = typeof value === 'string' ? value.split(',') : value;

    const selectedValue = values.length > 0 ? [values[0]] : [];

    if (selectedValue.length > 0) {
      setFilter((prevState) => {
        return { ...prevState, offset: 0, orderBy: selectedValue };
      });
    } else {
      setFilter((prevState) => {
        const newState = { ...prevState, offset: 0 };
        delete newState['orderBy'];
        return newState;
      });
    }
    setPage(1);
  };

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setFilter((prevState) => ({
      ...prevState,
      offset: prevState.pageSize * pageNumber - prevState.pageSize,
    }));
  };

  const monitoredPlatforms = new Set(
    monitorings
      .map((monitoring) => {
        return monitoring.platform;
      })
      .filter((platform) => platform !== 'stepstone')
  );

  const handlePlatformChange = (event: SelectChangeEvent) => {
    if (event.target.value) {
      setFilter((prevState) => {
        return { ...prevState, offset: 0, platform: event.target.value };
      });
    } else {
      setFilter((prevState) => {
        const newState = { ...prevState, offset: 0 };
        delete newState['platform'];
        return newState;
      });
    }
    setPage(1);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'row' : 'column',
          alignItems: isSmallScreen ? 'flex-start' : 'center',
          justifyContent: 'space-between',
        }}
      >
        <TrustpageReviewCount reviewCount={reviewsResponse.totalItems} />
        <TrustpageFilterSort
          platform={filter.platform || ''}
          orderBy={filter.orderBy || []}
          monitoredPlatforms={Array.from(monitoredPlatforms)}
          onPlatformChange={handlePlatformChange}
          onOrderByChange={handleOrderByChange}
        />
      </Box>
      <Box sx={{ mt: theme.spacing(2) }}>
        {reviews.map((reviewEntry: IReviewNew) => {
          if (reviewEntry.platform === 'stepstone') {
            return null;
          }

          const communityManagement = {} as CommunityManagement;

          const review = {
            facebook: (
              <FacebookReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            golocal: (
              <GolocalReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                communityManagement={communityManagement}
                reviewEntry={reviewEntry}
              />
            ),
            aerzte: (
              <AerzteReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            ekomi: (
              <EKomiReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            kununu: (
              <KununuReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            holidaycheck: (
              <HolidayCheckReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            tripadvisor: (
              <TripAdvisorReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            hotels: (
              <HotelsReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            amazonde: (
              <AmazondeReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            appstore: (
              <AppstoreReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            playstore: (
              <PlaystoreReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            airbnb: (
              <AirbnbReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            ebay: (
              <EbayReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            capterra: (
              <CapterraReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            indeed: (
              <IndeedReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            expedia: (
              <ExpediaReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            booking: (
              <BookingReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            idealo: (
              <IdealoReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            trustedshops: (
              <TrustedShopsReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            sanego: (
              <SanegoReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            yelp: (
              <YelpReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            trustpilot: (
              <TrustPilotReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            jameda: (
              <JamedaReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            glassdoor: (
              <GlassdoorReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            shopauskunft: (
              <ShopauskunftReview
                key={reviewEntry.id}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            google: (
              <GoogleReview
                key={reviewEntry.id}
                commentingModule={false}
                locationId={''}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            googleplace: (
              <GooglePlace
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            mobile: (
              <MobileReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            trivago: (
              <TrivagoReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            fewo: (
              <FewoReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            autoscout24: (
              <AutoscoutReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            tellows: (
              <TellowsReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            check24: (
              <Check24
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            gutebanken: (
              <GutebankenReview
                key={reviewEntry.id}
                commentingModule={false}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            jobvoting: (
              <JobvotingReview
                key={reviewEntry.id}
                commentingModule={commentingModule}
                communityManagement={communityManagement}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            omr: (
              <OmrReview
                communityManagement={communityManagement}
                commentingModule={false}
                hideHeader={false}
                hideFooter={false}
                reviewEntry={reviewEntry}
              />
            ),
            default: null,
          };
          return review[reviewEntry.platform as keyof typeof review] ?? review['default'];
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: theme.spacing(2) }}>
        <Pagination
          count={reviewsResponse.pagesTotal}
          page={page}
          color="primary"
          variant="text"
          onChange={handlePageNumberChange}
          sx={{
            '& .MuiPaginationItem-root.Mui-selected': {
              color: primaryColor
                ? theme.palette.getContrastText(primaryColor)
                : theme.palette.primary.main,
            },
            '& .MuiPaginationItem-root:hover': {
              backgroundColor: primaryColor
                ? alpha(hexToRgb(primaryColor), 0.2)
                : theme.palette.divider,
            },
          }}
        />
      </Box>
    </>
  );
};

export default TrustpageReviews;
